import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import VideoPlayer from 'components/VideoPlayer/VideoPlayer';
import { useCourseById } from '../../../hooks/course/useCourseById';
import { useParams } from 'react-router-dom';
import AccessDenied from '../../../components/access/AccessDenied';

const BoasVindas: React.FC = () => {
  const { user } = useAuth();
  const { courseId } = useParams<{ courseId: string }>();
  const { course, error, errorStatusCode } = useCourseById(courseId);

  const [hasCompletedWelcome, setHasCompletedWelcome] = useState(
    localStorage.getItem('welcome_completed') === 'true'
  );

  const handleCompleteWelcome = () => {
    localStorage.setItem('welcome_completed', 'true');
    setHasCompletedWelcome(true);
  };

  if (error && (errorStatusCode === 403 || errorStatusCode === 404)) {
    return <AccessDenied message={error} statusCode={errorStatusCode} />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-xl p-8 shadow-sm text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
          <span className="text-4xl">🌟</span>
          Bem-vinda ao Clube do Livro no Divã, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-lg text-gray-700">
          Que alegria ter você aqui nesse espaço tão poderoso!
        </p>
      </div>

      {/* Welcome Message */}
      <div className="bg-white rounded-xl p-8 shadow-sm">
        <div className="prose max-w-none">
          <div className="text-center mb-8">
            {course?.videoUrl && 
              <VideoPlayer 
                  url={course?.videoUrl} 
                  poster={""}
              />
            }
            
            <h2 className="text-2xl font-bold text-gray-900 mt-4 mb-4">
              Aqui não é você que lê o livro, é o livro que te lê
            </h2>
          </div>

          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p className="text-lg">
              É uma alegria ter você aqui nesse espaço que foi tão sonhado por mim e desenvolvido para ser uma comunidade de mulheres que inspiram, acolhem e fortalecem a si mesmas e às outras. Aqui, a gente aprende nos livros a se desconhecer pra se conhecer. E é assim que a gente se encontra, se perde e se acha, compartilhando experiências, desejos, perspectivas, estranhezas e semelhanças e, principalmente, exercitando juntas o bonito trabalho de ser protagonista da nossa história.
            </p>

            <p>
              Esse é um espaço seguro: aqui você será ouvida, acolhida e respeitada e, para que isso seja possível, contamos com algumas Regras da Comunidade, que garantem que aqui seja um lugar de confiança e cuidado mútuo. E, a partir do momento em que você entra aqui, você concorda com todas elas.
            </p>

            <p>Abrimos um grupo no WhatsApp para facilitar os comunicados importantes, como aulas novas e mudanças de data dos encontros. Clique aqui pra participar!  
              <a
                href="https://chat.whatsapp.com/GA6aQTAOb93GYxVPZEiP6q?mode=gi_t"
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:underline"
                >
                <strong> Clique aqui pra participar!</strong>
              </a>
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
              <h3 className="font-semibold text-gray-900 mb-3">✨ Regras da Comunidade:</h3>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Privacidade</strong> - o seu acesso a comunidade é individual e intransferível, não podendo haver compartilhamento de contas.</li>
                <li><strong>Respeito absoluto</strong> – divergências são bem-vindas, mas nunca ofensas pessoais.</li>
                <li><strong>Confidencialidade</strong> – tudo que é compartilhado aqui fica aqui. Nenhuma informação pode ser divulgada fora da comunidade, assim como prints não podem ser feitos e compartilhados.</li>
                <li><strong>Responsabilidade</strong> -  você terá integral responsabilidade pela segurança de qualquer informação a que tiver acesso aqui na comunidade e, em caso de infração da regra de confidencialidade, você terá responsabilidade integral por qualquer informação que distribuir.</li>
                <li><strong>Ambiente livre de discriminação</strong> – não toleramos preconceito, assédio ou qualquer forma de violência.</li>
                <li><strong>Segurança</strong> – não compartilhe nomes ou dados que possam identificar terceiros.</li>
                <li>Todas as mensagens na comunidade são escritas por membros individuais da comunidade. Portanto,
                    representam opiniões individuais, não necessariamente a opinião da Manuela Xavier. A Manuela Xavier
                    não é responsável por atos ou omissões dos membros, incluindo arquivos, informações e/ou materiais 
                    que eles disponibilizem no site. A Manuela Xavier não é responsável por qualquer dano resultante do 
                    uso de qualquer material compartilhado por membros da comunidade e não é responsável pela precisão 
                    ou completude de tal material. A Comunidade não é responsável por membros que agirem contra a lei 
                    ou de maneira danosa; não possui controle ou domínio sobre as atividades dos membros da comunidade;
                    e não é obrigada a compensar quaisquer danos resultantes de qualquer postagem na comunidade. A Comunidade
                    não exerce nenhum controle sobre outros sites e recursos online, e não é responsável pelo conteúdo, publicidade,
                    produtos ou outros materiais divulgados por outros membros na comunidade  e não será responsável, direta ou indiretamente,
                    por qualquer dano ou perda resultante de tais conexões. Seu acesso e uso de sites por meio de links, incluindo informação,
                    material, produtos e serviços neles contidos, é o seu próprio critério.
                    A sua continuação de uso da comunidade, após a entrada na plataforma da comunidade, significará que você aceita os termos acima citados.
                </li>
                <li><strong>Aviso (dados sensíveis):</strong> – : ao publicar um relato pessoal na comunidade, você declara automaticamente que leu, está ciente e concorda com este aviso e com o tratamento do conteúdo publicado para fins de funcionamento e segurança da comunidade (inclui moderação, análises de denúncias e eventual remoção). Recomendamos que compartilhe apenas o que se sentir confortável em tornar acessível a outras participantes. Apesar dos nossos esforços de moderação e segurança, não é possível impedir tecnicamente capturas de tela (prints) feitas por terceiros.</li>
              </ul>
            </div>
          </div>
        </div>

        {!hasCompletedWelcome && (
          <div className="mt-8 text-center">
            <button
              onClick={handleCompleteWelcome}
              className="px-8 py-3 bg-terracota text-white rounded-lg font-semibold hover:bg-marrom-escuro transition-colors shadow-md"
            >
              🌟 Estou pronta para começar!
            </button>
          </div>
        )}

        {hasCompletedWelcome && (
          <div className="mt-8 p-4 bg-green-100 rounded-lg text-center">
            <span className="text-green-700 font-medium">
              ✅ Bem-vindas concluído! Continue explorando as próximas seções.
            </span>
          </div>
        )}
      </div>

      {/* Contact Info */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            💌 Precisa de ajuda?
          </h3>
          <p className="text-gray-700 mb-4">
            Nossa equipe está sempre disponível para te apoiar nesta jornada.
          </p>
          <div className="flex justify-center gap-4">
            <span className="px-4 py-2 bg-white rounded-lg shadow-sm text-sm text-gray-600">
              📧 Suporte: equipe@manuelaxavier.com.br
            </span>
            <span className="px-4 py-2 bg-white rounded-lg shadow-sm text-sm text-gray-600">
              💬 WhatsApp: (11) 97383-7444
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoasVindas;